import {
  ColumnFiltersState,
  Table as ITable,
  SortingState,
  VisibilityState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect } from "react";

import EmptyData from "../../state-pages/EmptyData";
import {
  RTKQueryHook,
  RenderConfig,
} from "../async-select/async-single-select";

import TableFunctions from "./Functions";
import TableHeader from "./TableHeader";
import TableRow, { EmptyTableRow } from "./TableRow";
import Filters from "./filter/Filters";
import Paginator from "./paginator";

import { useTheme } from "@/components/theme-provider";
import * as Table from "@/components/ui/table";
import { TEN } from "@/constants/constants";
import { ResponsePagination } from "@/hooks/usePagination";
import useQueryParams from "@/hooks/useQueryParams";
import { GlobalRowDataTypes } from "@/interfaces/global-table.interface";
import {
  ExtendedColumnDef,
  _TFloatingMenuCustomComponentProps,
} from "@/interfaces/globals";
import { IbtnVariant, TOptions } from "@/interfaces/interfaces";
import { cn } from "@/lib/utils";

export type FilterConfig<TQueyResponseType, TQueryBaseParams> = {
  label?: string;
  linked?: string;
  options?: TOptions[];
  multiSelect?: boolean;
  isPaginatedSingleSelect?: boolean;
  paginatedSelectConfig?: {
    useQuery: RTKQueryHook<TQueyResponseType, TQueryBaseParams>;
    baseQuery: TQueryBaseParams;
    renderConfig: RenderConfig<TQueyResponseType>;
  };
};

export type FilterType<TQueyResponseType, TQueryBaseParams> = {
  [key: string]: FilterConfig<TQueyResponseType, TQueryBaseParams> | boolean;
};

type TEmptyData = {
  // title: string;
  subtitle?: string;
  small?: boolean;
  btnAction?: () => void;
  btnLabel?: string;
  btnVariant?: IbtnVariant;
  icon?: string;
};

type TAddButton = {
  label?: string;
  variant?: IbtnVariant;
  className?: string;
  handleClick?: () => void;
  icon?: string;
  disabled?: boolean;
};

export interface TFunctions<TData, TValue> {
  hide?: boolean;
  isExport?: boolean;
  search?: string;
  pagination?: ResponsePagination;
  columnToggle?: boolean;
  add?: TAddButton | false;
  onRowClick?: (id: string) => void;
  RightClickData?: (row: GlobalRowDataTypes<TValue>) => ActionButtonDataTypes[];
  floatingMenuClassName?: string;
  floatingMenu?: boolean;
  floatingMenuCustom?: React.ComponentType<_TFloatingMenuCustomComponentProps>;
  hasExportInFloatingMenu?: boolean;
  showRenderableFunctions?: boolean;
  multiFloating?: {
    // title: string;
    multiIds: string[];
    handleAddTable: (key: string, table: ITable<TData>) => void;
    handleUnSelectAll: () => void;
  };
  custom?: {
    node: React.ReactNode;
    position: "left" | "right" | "center";
  };
}

interface DataTableProps<TData, TValue, TQueyResponseType, TQueryBaseParams> {
  title: string;
  data: TData[];
  columns: ExtendedColumnDef<TData, TValue>[];
  empty: TEmptyData;
  customEmptyLayout?: React.ReactNode;
  floatingBar?: React.ReactNode | null;
  filters?: FilterType<TQueyResponseType, TQueryBaseParams> | boolean;
  hideFilterRow?: boolean;
  hideFilterTabs?: boolean;
  hasWritePermission?: boolean;
  onTableInstanceChange?: (instance: ITable<TData>) => void;
  id?: string;
  customProperties?: {
    handleAddIds?: (ids: string[]) => void;
    newRowSlot?: React.ReactNode;
  };

  functions?: TFunctions<TData, TValue>;
  hide: {
    functions: boolean;
    filters: boolean;
  };
}

export const CustomTable = <
  TData extends { id?: string },
  TValue,
  TQueyResponseType,
  TQueryBaseParams,
>({
  title,
  data = [],
  columns,
  empty,
  customEmptyLayout,
  // searchKey,
  id,
  filters,
  hasWritePermission = false,
  customProperties,
  functions,
  hide,
  onTableInstanceChange,
}: DataTableProps<
  TData,
  TValue,
  TQueyResponseType,
  TQueryBaseParams
>): React.JSX.Element => {
  const filteredColumns = columns.filter((column) => !column.hideColumn);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );

  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const { theme: skin } = useTheme();

  const filterOperation = useQueryParams();

  const table = useReactTable({
    getRowId: (row) => (row.id ? row.id : ""),
    data,
    columns: filteredColumns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    sortDescFirst: false,
    initialState: {
      pagination: {
        pageSize: 10, //Follow UP: If server sided pagination get merge we need to look out this how it works.
      },
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility: {
        ...filteredColumns.reduce((acc, column) => {
          if (column?.id && column?.enableHiding) {
            acc[column.id] = !column.enableHiding;
          }

          return acc;
        }, {} as VisibilityState),
        ...columnVisibility,
      },
      rowSelection,
    },
    defaultColumn: {
      minSize: 0,
      size: Number.MAX_SAFE_INTEGER,
      maxSize: Number.MAX_SAFE_INTEGER,
    },
  });

  const renderableFunctions = Object.keys(functions || {}).filter((key) =>
    [
      "export",
      "search",
      "columnToggle",
      "add",
      "custom",
      "floatingMenu",
    ].includes(key),
  );

  useEffect(() => {
    if (functions?.multiFloating) {
      functions?.multiFloating.handleAddTable(title, table);
    }
    if (onTableInstanceChange && table) {
      onTableInstanceChange(table);
    }
  }, [table, onTableInstanceChange]);

  const getContent = ():
    | string
    | number
    | true
    | Iterable<React.ReactNode>
    | JSX.Element => {
    if (customEmptyLayout) {
      return customEmptyLayout;
    }

    if (!customProperties?.newRowSlot) {
      return (
        <EmptyData
          hideButton={!hasWritePermission}
          title={title}
          small={empty?.small}
          subtitle={empty?.subtitle}
          btnAction={
            empty.btnAction || (functions?.add as TAddButton)?.handleClick
          }
          btnLabel={empty.btnLabel || (functions?.add as TAddButton)?.label}
          btnVariant={
            empty.btnVariant || (functions?.add as TAddButton)?.variant
          }
        />
      );
    }

    return "";
  };

  return (
    <>
      <div className='w-full'>
        {(filters || renderableFunctions.length > 0) && (
          <div className={cn("flex justify-between gap-4 flex-wrap-reverse")}>
            {filters && (
              <Filters
                filters={
                  filters as FilterType<TQueyResponseType, TQueryBaseParams>
                }
                showFilters={true}
                filterOperation={filterOperation}
                hide={hide?.filters}
              />
            )}
            {renderableFunctions.length > 0 && (
              <div className={cn("ml-auto mb-2")}>
                <TableFunctions
                  table={table}
                  columns={filteredColumns}
                  data={data}
                  hasWritePermission={hasWritePermission}
                  filterOperation={filterOperation}
                  functions={functions}
                  hide={hide?.functions}
                />
              </div>
            )}
          </div>
        )}

        <div className='border rounded-md '>
          <Table.Table id={id}>
            <Table.TableHeader
              className={cn(
                data?.length && "group/header",
                skin !== "dark" ? "bg-[#E4E8F2]" : "bg-primaryLight",
              )}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <TableHeader
                  key={headerGroup?.id}
                  headerGroup={headerGroup}
                  pagination={functions?.pagination}
                />
              ))}
            </Table.TableHeader>
            <Table.TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => {
                  return (
                    <TableRow
                      key={`${row.id}-${Math.random()}`}
                      title={title}
                      row={row}
                      onRowClick={functions?.onRowClick}
                      RightClickData={
                        hasWritePermission
                          ? functions?.RightClickData
                          : undefined
                      }
                    />
                  );
                })
              ) : (
                <EmptyTableRow
                  key={`empty-row-${id}`}
                  customProperties={!!customProperties?.newRowSlot}
                  getContent={getContent}
                />
              )}
              {customProperties?.newRowSlot && customProperties.newRowSlot}
            </Table.TableBody>
          </Table.Table>
        </div>

        {/* {functions?.pagination && ( */}
        <div className={cn(`items-center py-4 flex`)}>
          {(table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) && (
            <div className='flex-1 text-sm whitespace-nowrap text-muted-foreground'>
              {table?.getFilteredSelectedRowModel()?.rows?.length} of{" "}
              {table?.getFilteredRowModel()?.rows?.length} row(s) selected.
            </div>
          )}
          <div className='flex justify-end w-full'>
            {((functions?.pagination && functions?.pagination?.totalResult) ||
              data?.length) > TEN && (
              <Paginator
                table={table}
                currentPage={
                  functions?.pagination?.currentPage ??
                  table?.getState()?.pagination?.pageIndex + 1
                }
                totalPages={
                  functions?.pagination?.totalPages ?? table?.getPageCount()
                }
                onPageChange={(pageNumber) => {
                  table?.setPageIndex(pageNumber - 1);
                  functions?.pagination?.handlePageUpdate(pageNumber);
                }}
                showPreviousNext
                pagination={functions?.pagination}
              />
            )}
          </div>
        </div>
        {/* )} */}
      </div>
    </>
  );
};
